import {
  AbstractMesh,
  Color3,
  GroundMesh,
  Material,
  Mesh,
  MeshBuilder,
  Nullable,
  PBRMaterial,
  Texture,
  TransformNode,
  Vector3,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, Zed } from '@modules/core';
import { addMaterialPlugin, clearArchetype, createEntityFactory, GLTFLoader } from '@modules/standard';
import { World } from 'miniplex';

import { DanjiDecalMaterialPlugin } from '../../graphic/material/DecalMaterialPlugin';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { TourDanjiFetcher } from '../../TourDanjiFetcher';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiOptions } from '../../TourDanjiModule';
import { MeshType } from '../component/data/DanjiMesh';
import { Entity as BaseEntity } from '../Entity';

import { RaycastSystem } from './RaycastSystem';

export type GisEntity = {
  GisComplex?: {
    areaDanjiId: number;
    areaDanjiName: string;
    buildingIds: number[];
    danjiCenter: { lat: number; lng: number };
    underConstruction: boolean;
    사용승인일: string;
    세대수: number;
  };
};

type Entity = BaseEntity & GisEntity;

/** 남한면적중심마을 장연리  */
const defaultLatLng = {
  lat: 36.358949,
  lng: 127.7646949,
};

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class GroundSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('GroundSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly danjiOptions;

  private readonly world;
  private readonly groundArchetype;
  private readonly decalArchetype;
  private readonly scene;
  private readonly danjiFetcher;
  private readonly gltfLoader;
  private readonly raycastSystem;
  private readonly materialManager;

  private readonly createEntity;

  private generatedBaseDecalMesh?: AbstractMesh;

  //-----------------------
  private _cameraBoundingSize: number = 0;
  private _cameraBoundingOffset: Vector3 = Vector3.Zero();
  public get cameraBoundingSize() {
    return this._cameraBoundingSize;
  }
  public get cameraBoundingOffset() {
    return this._cameraBoundingOffset;
  }

  public get groundMesh() {
    return this.groundArchetype.entities.length > 0 ? this.groundArchetype.entities?.at(0)?.Ground.mesh : undefined;
  }

  public get extends() {
    if (this.decalArchetype.entities.length > 0) {
      return this.decalArchetype.entities?.at(0)?.Decal.extends;
    } else if (this.groundArchetype.entities.length > 0) {
      return this.groundArchetype.entities?.at(0)?.Ground.extends;
    } else {
      return this.scene.getWorldExtends();
    }
  }

  constructor({
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,

    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;

    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    DanjiDecalMaterialPlugin.register();

    this.danjiOptions = danjiOptions;

    this.world = world;
    this.scene = babylonjs.scene;
    this.danjiFetcher = danjiFetcher;
    this.gltfLoader = gltfLoader;
    this.raycastSystem = raycastSystem;
    this.materialManager = materialManager;

    this.createEntity = createEntityFactory(this.world);

    this.groundArchetype = world.with('Ground');
    this.decalArchetype = world.with('Decal');
  }

  dispose() {
    DanjiDecalMaterialPlugin.unregister();

    this.generatedBaseDecalMesh?.dispose(false, true);
    clearArchetype(this.groundArchetype);
  }

  // GIS
  public async generateGround(danjiId: string, parentTransform: TransformNode) {
    try {
      const result = await this.gltfLoader.loadModels<Entity>(
        this.scene,
        [
          {
            src: `gis/${danjiId}.glb`,
            modelName: '__root__',
            parentTransform: parentTransform,
            resourceServer: true,
          },
        ],
        undefined,
        {
          autoCompileShader: true,
          autoLoadTexture: true,
        }
      );

      const imported = result[0]?.model;
      const root = result[0]?.root || MeshBuilder.CreateGround('Ground', { width: 10_000, height: 10_000 });
      if (!imported) {
        console.error('Failed to load gis mesh');
      }

      root.parent = parentTransform;
      root.name = 'Ground';
      root.position = Vector3.ZeroReadOnly;

      const terrain = imported?.meshes.find((m) => m.id === 'EXPORT_DAUM_MAP_2D_DAUM') || root;
      const complexes = imported?.transformNodes.find((t) => t.name === 'Complexes');
      const complexMeshes = (complexes?.getChildMeshes() ?? []) as Mesh[];
      const otherBuildings = imported?.meshes.find((t) => t.name === 'OtherBuildings') as Mesh | undefined;

      if (!(terrain instanceof Mesh)) {
        console.error('Failed to load gis terrain');
        return;
      }

      const material = this.materialManager.createMaterial(MaterialType.GIS_Map);
      if (terrain.material && terrain.material instanceof PBRMaterial) {
        material._albedoColor = new Color3(1, 1, 1).toLinearSpace();
        const albedoTexture = terrain.material.albedoTexture?.clone();
        if (albedoTexture) {
          material.albedoTexture = albedoTexture;
          material.albedoTexture.level = 0.55;
        }
        const ambientTexture = terrain.material.albedoTexture?.clone();
        if (ambientTexture) {
          material.ambientTexture = ambientTexture;
          material.ambientTexture.level = 1;
        }
        terrain.material.dispose(true, true);
      }

      terrain.sideOrientation = Mesh.DEFAULTSIDE;
      terrain.material = material;

      if (terrain instanceof GroundMesh) {
        terrain.createNormals(false);
        terrain.flipFaces(false);
        material.albedoColor = new Color3(0.902, 0.902, 0.882);
      }

      const buildingMaterial = this.materialManager.createMaterial(MaterialType.GIS_Building);

      if (otherBuildings) {
        otherBuildings.createNormals(false);
        otherBuildings.convertToFlatShadedMesh();
        otherBuildings.material?.dispose(false, false);
        otherBuildings.material = buildingMaterial;
      }

      for (const c of complexMeshes) {
        c.createNormals(false);
        c.convertToFlatShadedMesh();
        c.material?.dispose(false, false);
        c.material = buildingMaterial;

        const boundingInfo = c.getHierarchyBoundingVectors();
        const guiPosition = boundingInfo.max.add(boundingInfo.min).scale(0.5);
        guiPosition.y = boundingInfo.max.y;

        this.createEntity({
          GisComplex: c.metadata?.gltf?.extras,
          TransformNode: c,
          GUIDisplayable: {
            position: guiPosition,
            occludee: true,
          },
        });
      }

      this.raycastSystem.bindGroundMesh(terrain);

      const danjiCenter = this.danjiFetcher.danjiData?.danjiCenter;
      if (!danjiCenter) {
        console.error(new Error('The `danjiCenter` is not available. Default LatLng will be used as a fallback.'));
      }

      const { lat: latitude, lng: longitude } = danjiCenter ?? defaultLatLng;

      this.createEntity<Omit<Entity, 'entityId'>>({
        Ground: {
          // 편의를 위해 데이터 중복
          mesh: terrain,
          extends: terrain.getHierarchyBoundingVectors(),
          latitude,
          longitude,
        },
        DanjiMeshes: {
          danjiMeshes: [
            { type: MeshType.GIS_GROUND, meshes: [terrain] },
            {
              type: MeshType.GIS_BUILDING,
              meshes: [...(otherBuildings ? [otherBuildings] : []), ...complexMeshes],
            },
          ],
        },
      });
    } catch (error) {
      console.error(error);
    }
  }

  // Decals
  public getBaseDecalMesh(parentTransform: Nullable<TransformNode> = null) {
    if (!this.generatedBaseDecalMesh) {
      const hit = this.raycastSystem.pickWithRayByLand(0, 0);

      let y = 0;
      if (hit?.hit) {
        y = hit.pickedPoint!.y;
      }

      const decalData = this.danjiFetcher.decalData;
      const x = decalData?.offsetWidthMeter ?? 0;
      const z = decalData?.offsetHeightMeter ?? 0;
      const position = new Vector3(-x!, y, -z!);
      const scale = new Vector3(decalData?.widthMeter, decalData?.heightMeter, Number.MAX_VALUE);

      if (!this.groundMesh) throw new Error('Ground mesh is not available');
      const decalables = this.scene.getMeshesByTags('decalable', (m) => m instanceof Mesh) as Mesh[];
      decalables.forEach((m) => {
        // because ground model from gltf is lefthand side and decalable created in bjs is righthand side
        m.flipFaces(false);
        m.sideOrientation = this.groundMesh?.sideOrientation ?? Mesh.DEFAULTSIDE;
      });
      let decalableMesh = decalables.length
        ? Mesh.MergeMeshes([this.groundMesh, ...decalables], false)?.flipFaces(false)
        : this.groundMesh;

      if (!decalableMesh) {
        decalableMesh = this.groundMesh.clone();
        console.error('Failed to merge decalable meshes');
      }

      this.generatedBaseDecalMesh = MeshBuilder.CreateDecal('BaseDecal', decalableMesh, {
        position: position,
        normal: Vector3.UpReadOnly,
        size: scale,
        angle: -Math.PI / 2,
        cullBackFaces: true,
      });
      if (decalables.length) decalableMesh.dispose();
      this.generatedBaseDecalMesh.name = 'Decal_BaseMesh';
      this.generatedBaseDecalMesh.material?.dispose();
      this.generatedBaseDecalMesh.setEnabled(false);
      this.generatedBaseDecalMesh.setParent(parentTransform);

      const disposables = this.scene.getMeshesByTags('disposeAfterDecal');
      disposables.forEach((m) => m.dispose());
    }

    return this.generatedBaseDecalMesh!;
  }

  public async generateDecals(parentTransform: TransformNode) {
    const decalData = this.danjiFetcher.decalData;
    const enviromentMaskURL = this.danjiFetcher.enviromentMaskURL;
    if (!decalData || !enviromentMaskURL) return;

    const hit = this.raycastSystem.pickWithRayByGround(0, 0);

    let y = 0;
    if (hit?.hit) {
      y = hit.pickedPoint!.y;
    }

    const maskTexture = new Texture(
      enviromentMaskURL,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      (message, exception) => {
        console.log(`${message}\n${exception}`);
      }
    );

    const x = decalData.offsetWidthMeter;
    const z = decalData.offsetHeightMeter;
    const decalNormal = hit == null ? new Vector3(0, 1, 0) : hit.getNormal();
    const decalPosition = new Vector3(-x!, y, -z!);
    const decalSize = new Vector3(decalData.widthMeter, decalData.heightMeter, 1000);
    const decals = await Promise.all([
      this.generateDecal(parentTransform, MeshType.DECAL_CAR_ROAD, maskTexture, decalPosition, decalNormal!, decalSize),
      this.generateDecal(
        parentTransform,
        MeshType.DECAL_WALK_ROAD,
        maskTexture,
        decalPosition,
        decalNormal!,
        decalSize
      ),
      this.generateDecal(
        parentTransform,
        MeshType.DECAL_PLAYGROUND,
        maskTexture,
        decalPosition,
        decalNormal!,
        decalSize
      ),
      this.generateDecal(parentTransform, MeshType.DECAL_RECYCLE, maskTexture, decalPosition, decalNormal!, decalSize),
      this.generateDecal(parentTransform, MeshType.DECAL_GRASS, maskTexture, decalPosition, decalNormal!, decalSize),
      this.generateDecal(parentTransform, MeshType.DECAL_WATER, maskTexture, decalPosition, decalNormal!, decalSize),
    ]);

    const decal = decals.find((d) => d !== undefined);

    if (decal) {
      this._cameraBoundingSize = decal.getBoundingInfo().boundingSphere.radius * 1.5;
      this._cameraBoundingOffset = new Vector3(x, 0, z);
    }
  }
  private async generateDecal(
    parentTransform: TransformNode,
    type: MeshType,
    maskTexture: Texture,
    localPosition: Vector3,
    _normal: Vector3,
    scale: Vector3
  ) {
    let material: Nullable<PBRMaterial> = null;
    let maskLayer1 = 0;
    let maskLayer2 = 0;
    let maskLayer3 = 0;
    let maskLayer4 = 0;

    switch (type) {
      case MeshType.DECAL_GRASS:
        material = this.materialManager.createMaterial(MaterialType.Decal_Grass_Summer);
        maskLayer1 = 240;
        maskLayer2 = 75;
        break;
      case MeshType.DECAL_CAR_ROAD:
        material = this.materialManager.createMaterial(MaterialType.Decal_CarRoad);
        maskLayer1 = 45;
        maskLayer2 = 105;
        break;
      case MeshType.DECAL_WALK_ROAD:
        material = this.materialManager.createMaterial(MaterialType.Decal_WalkRoad);
        maskLayer1 = 30;
        break;
      case MeshType.DECAL_PLAYGROUND:
        material = this.materialManager.createMaterial(MaterialType.Decal_PlayGround);
        maskLayer1 = 60;
        break;
      case MeshType.DECAL_RECYCLE:
        material = this.materialManager.createMaterial(MaterialType.Decal_Recycle);
        maskLayer1 = 90;
        break;
      case MeshType.DECAL_WATER:
        material = this.materialManager.createMaterial(MaterialType.Decal_Water);
        maskLayer1 = 150;
        break;
    }
    if (!material) {
      console.log(`${type}의 material이 존재하지 않습니다.`);
      return;
    } else {
      const decalMaterial = addMaterialPlugin(
        material,
        DanjiDecalMaterialPlugin.materialName,
        {}
      )[0] as DanjiDecalMaterialPlugin;
      decalMaterial.maskEdge = false;
      decalMaterial.maskLayer1 = maskLayer1;
      decalMaterial.maskLayer2 = maskLayer2;
      decalMaterial.maskLayer3 = maskLayer3;
      decalMaterial.maskLayer4 = maskLayer4;
      decalMaterial.maskTexture = maskTexture;
    }
    const decalData = this.danjiFetcher.decalData;
    const enviromentMaskURL = this.danjiFetcher.enviromentMaskURL;
    const groundMesh = this.groundMesh;
    if (!decalData || !enviromentMaskURL || !groundMesh) return undefined;

    material.zOffset = -2;

    const decalMesh = this.getBaseDecalMesh(parentTransform).clone(material.name, null);

    if (decalMesh) {
      decalMesh.setParent(parentTransform);

      decalMesh.material = material;
      if (decalMesh.material.name !== 'Decal_RoadEdge') {
        // z-fighting 방지
        decalMesh.position = decalMesh.position.add(new Vector3(0, 0.25, 0));
      }

      this.createEntity<Omit<Entity, 'entityId'>>({
        ...{
          Decal: {
            extends: decalMesh.getHierarchyBoundingVectors(),
          },
          DanjiMeshes: {
            danjiMeshes: [{ type: type, meshes: [decalMesh] }],
          },
        },
      });

      decalMesh.setEnabled(true);
      await decalMesh.material.forceCompilationAsync(decalMesh);

      return decalMesh;
    }

    return undefined;
  }

  readonly operations = [];
}
